import { IntegrationProvider } from "@repo/app-types/entities/integration";

// Integration metadata, in its own module so both the settings section and the
// breadcrumb label can read it without importing the section component.
export type IntegrationConfig = {
  provider: IntegrationProvider;
  slug: string;
  name: string;
  description: string;
  disconnectWarning: string;
};

// Registry of the integrations rendered in the overview table and detail pages.
// Add an entry to surface a new integration.
export const integrationConfigs: IntegrationConfig[] = [
  {
    provider: IntegrationProvider.SLACK,
    slug: "slack",
    name: "Slack",
    description:
      "Connect Slack to send automation results to channels or directly to teammates.",
    disconnectWarning:
      "Disconnecting Slack will stop automation results from being delivered to Slack.",
  },
  {
    provider: IntegrationProvider.TEAMS,
    slug: "microsoft-teams",
    name: "Microsoft Teams",
    description:
      "Connect Microsoft Teams to send automation results to channels or directly to teammates.",
    disconnectWarning:
      "Disconnecting Microsoft Teams will stop automation results from being delivered to Teams.",
  },
  {
    provider: IntegrationProvider.GURU,
    slug: "guru",
    name: "Guru",
    description:
      "Sync Guru collections into Alpharun knowledge bases so agents answer from the same articles your team does. Each collection becomes its own read-only knowledge base, kept up to date in the background.",
    disconnectWarning:
      "Disconnecting Guru will delete its synced knowledge bases and documents.",
  },
  {
    provider: IntegrationProvider.KUSTOMER,
    slug: "kustomer",
    name: "Kustomer",
    description:
      "Connect Kustomer so its knowledge bases are synced into read-only Alpharun knowledge bases.",
    disconnectWarning:
      "Disconnecting Kustomer will delete its synced knowledge bases and documents.",
  },
  {
    provider: IntegrationProvider.GENESYS,
    slug: "genesys",
    name: "Genesys Cloud",
    description:
      "Import Genesys Cloud call recordings into Alpharun for transcription, coaching, and performance analysis.",
    disconnectWarning:
      "Disconnecting Genesys Cloud will stop new call recordings from syncing.",
  },
  {
    // Synthetic: shown only for companies whose recordings we intake from an
    // Alpharun-owned S3 bucket (the boot payload carries the synthetic
    // integration for them). There is no connect/disconnect — the overview
    // special-cases it — so `disconnectWarning` is never surfaced.
    provider: IntegrationProvider.RECORDING_S3_INTAKE,
    slug: "recording-s3-intake",
    name: "Recording S3 intake",
    description:
      "Call recordings delivered to your Alpharun S3 bucket are imported for transcription, coaching, and performance analysis.",
    disconnectWarning: "",
  },
];

/** Resolves an integration's display name from its slug, or null if unknown. */
export function getIntegrationNameBySlug(
  slug: string | undefined,
): string | null {
  return (
    integrationConfigs.find((config) => config.slug === slug)?.name ?? null
  );
}
